import { useCallback, useMemo } from 'react';
import type { Dispatch, SetStateAction } from 'react';

import {
  addClient,
  addCqqlWithAClientUnder,
  addAllClientsForNewGov,
  findByKey,
  removeClient,
  setCustomersByRoleType,
  addAllOrRemoveAllClientsId,
  updateSystemRoles,
  updateIncludedClientIds,
  convertRoles,
} from 'modules/account/detail/helpers/utils';
import type {
  Client,
  UtilFunctions,
  CurrentSelectedRole,
  RolesToAssign,
  SingleGovAgencyItem,
} from 'modules/account/types';

interface UseRolesAssignmentHandlersParams {
  selectedRole: CurrentSelectedRole | undefined;
  setRolesToAssign: Dispatch<SetStateAction<RolesToAssign>>;
  unitId: string;
  isCQQL: boolean;
  forGovAgencyBusiness: SingleGovAgencyItem[];
  unitAddress?: { ward: string; operationCode: string };
  accountGovRoles: SingleGovAgencyItem[];
  hvsCompanyOperationCode: string;
  customerIdsByLocality: string[];
}

export const useRolesAssignmentHandlers = (params: UseRolesAssignmentHandlersParams) => {
  const {
    selectedRole,
    setRolesToAssign,
    unitId,
    isCQQL,
    forGovAgencyBusiness,
    unitAddress,
    accountGovRoles,
    hvsCompanyOperationCode,
    customerIdsByLocality,
  } = params;

  const handleSelectSingleCustomer = useCallback(
    (client: Client, isChecked: boolean): void => {
      if (!selectedRole) return;
      const { rolesType } = selectedRole;
      const roleId = selectedRole.role.id;
      const clientId = client.operationCode;

      if (rolesType === 'businessRoles' && isCQQL) {
        const roleToEdit = findByKey(forGovAgencyBusiness, roleId);
        setRolesToAssign((prev) =>
          roleToEdit
            ? updateIncludedClientIds(prev, roleToEdit, isChecked, clientId)
            : addCqqlWithAClientUnder(prev, clientId, { roleId, orgId: unitId })
        );
        return;
      }

      const targetRoleType = convertRoles(rolesType);
      setRolesToAssign((prev) => {
        const updated = { ...prev };
        const customers = updated.rolesAssignment[targetRoleType];
        updated.rolesAssignment[targetRoleType] = isChecked
          ? addClient(customers, { roleId, orgId: clientId })
          : removeClient(customers, { roleId, orgId: clientId });
        return updated;
      });
    },
    [selectedRole, isCQQL, forGovAgencyBusiness, unitId, setRolesToAssign]
  );

  const handleSelectAllCustomers = useCallback(
    (isChecked: boolean): void => {
      if (!selectedRole) return;
      const { rolesType } = selectedRole;
      const roleId = selectedRole.role.id;
      setRolesToAssign((prev) => {
        if (rolesType !== 'businessRoles')
          return setCustomersByRoleType(rolesType, prev, isChecked, roleId);
        if (isCQQL)
          return applyCqqlSelectAll(prev, forGovAgencyBusiness, roleId, unitId, isChecked);
        return setCustomersByRoleType('forClientBusiness', prev, isChecked, roleId);
      });
    },
    [selectedRole, isCQQL, forGovAgencyBusiness, unitId, setRolesToAssign]
  );

  const handleSelectSystemRole = useCallback(
    (roleId: string, add: boolean): void => {
      setRolesToAssign((prev) =>
        updateSystemRoles(prev, add, { roleId, orgId: hvsCompanyOperationCode })
      );
    },
    [hvsCompanyOperationCode, setRolesToAssign]
  );

  const handleToggleRoleFromList = useCallback(
    (roleId: string, rolesType: string, isChecked: boolean): void => {
      setRolesToAssign((prev) => {
        if (rolesType !== 'businessRoles')
          return setCustomersByRoleType(rolesType, prev, isChecked, roleId);
        if (isCQQL)
          return applyCqqlSelectAll(prev, forGovAgencyBusiness, roleId, unitId, isChecked);
        return setCustomersByRoleType('forClientBusiness', prev, isChecked, roleId);
      });
    },
    [isCQQL, forGovAgencyBusiness, unitId, setRolesToAssign]
  );

  const handleSelectCustomersByLocality = useCallback(
    (toggle: boolean): void => {
      if (!selectedRole || !unitAddress) return;
      setRolesToAssign((prev) =>
        !toggle
          ? removeLocalityFromSelectedRole(prev, selectedRole.role.id, accountGovRoles)
          : addLocalityToSelectedRole(
              prev,
              selectedRole.role.id,
              unitAddress,
              customerIdsByLocality,
              forGovAgencyBusiness
            )
      );
    },
    [
      selectedRole,
      unitAddress,
      accountGovRoles,
      customerIdsByLocality,
      forGovAgencyBusiness,
      setRolesToAssign,
    ]
  );

  const utilFunctions: UtilFunctions = useMemo(
    () => ({
      selectACustomer: handleSelectSingleCustomer,
      selectAllCustomer: handleSelectAllCustomers,
    }),
    [handleSelectSingleCustomer, handleSelectAllCustomers]
  );

  return {
    handleSelectSystemRole,
    handleSelectCustomersByLocality,
    handleToggleRoleFromList,
    utilFunctions,
  };
};

function applyCqqlSelectAll(
  prev: RolesToAssign,
  forGovAgencyBusiness: SingleGovAgencyItem[],
  roleId: string,
  unitId: string,
  isChecked: boolean
): RolesToAssign {
  const existingGovRole = findByKey(forGovAgencyBusiness, roleId);
  if (existingGovRole) return addAllOrRemoveAllClientsId(prev, existingGovRole, isChecked);
  if (!isChecked) return prev;
  return addAllClientsForNewGov(prev, { roleId, orgId: unitId });
}

function removeLocalityFromSelectedRole(
  rolesToAssign: RolesToAssign,
  selectedRoleId: string,
  accountGovRoles: SingleGovAgencyItem[]
): RolesToAssign {
  const updated = { ...rolesToAssign };
  updated.rolesAssignment.forGovAgencyBusiness = accountGovRoles.map((role) =>
    role.roleId !== selectedRoleId ? role : { ...role, includedClientByBoundary: false }
  );
  return updated;
}

function addLocalityToSelectedRole(
  rolesToAssign: RolesToAssign,
  selectedRoleId: string,
  unitAddress: { ward: string; operationCode: string },
  customerIdsByLocality: string[],
  forGovAgencyBusiness: SingleGovAgencyItem[]
): RolesToAssign {
  const updated = { ...rolesToAssign };
  const otherRoles = forGovAgencyBusiness.filter((role) => role.roleId !== selectedRoleId);
  updated.rolesAssignment.forGovAgencyBusiness = [
    ...otherRoles,
    {
      roleId: selectedRoleId,
      orgId: unitAddress.operationCode,
      includedClientIds: customerIdsByLocality,
      includedClientByBoundary: true,
    },
  ];
  return updated;
}
